<template>
    <div v-if="eventData.advertisedNewsletter && newsletterData">
        <div
            v-if="newsletter"
            class="card overflow-hidden">
            <div
                class="grid-container"
                :class="{'no-image': !newsletter?.background}">
                <div
                    v-if="newsletter?.background"
                    class="grid-area-image img-container">
                    <img
                        :src="newsletter.background.url"
                        alt="Image placeholder"
                        class="img-fit"
                        :style="'object-position: '+ newsletter.background.imageCenter || 'center center'">
                </div>
                <div class="grid-area-info">
                    <h2 class="mb-0">
                        <a
                            :href="newsletter.link"
                            class="d-inline-flex align-items-center gap-2"
                            target="_blank">
                            <img
                                v-if="newsletter?.image"
                                style="max-width: 9.375rem; max-height: 2rem;"
                                :src="newsletter.image.url"
                                alt="Image placeholder"
                                :style="'object-position: '+ newsletter.image.imageCenter || 'center center'">
                            <span>{{ newsletter.title }}</span>
                        </a>
                    </h2>
                    <small v-if="newsletter.teaser">
                        {{ newsletter.teaser }}
                    </small>
                </div>
            </div>
        </div>

        <p
            v-if="eventData.advertisedNewsletter.content"
            class="mt-2">
            <span v-html="eventData.advertisedNewsletter.content" />
        </p>

        <div
            v-if="subscribed"
            class="d-flex gap-4 align-items-center justify-content-center">
            <FontAwesomeIcon
                icon="check"
                size="2x"
                class="text-success" />
            <h2 class="mt-2">
                {{ $t('eventRegistration.subscriptionSuccessful') }}
            </h2>
        </div>
        <VForm
            v-else
            v-slot="{meta: {valid}}"
            @submit="subscribeToNewsletter">
            <BaseInput
                v-slot="{field}"
                v-model="newsletterData.email"
                :disabled="subscribing"
                :label="$t('eventRegistration.summary.newsletterEmail')"
                name="advertised-newsletter-email"
                :rules="{email: true}">
                <el-autocomplete
                    v-bind="field"
                    class="w-100"
                    size="large"
                    :disabled="subscribing"
                    :placeholder="$t('eventRegistration.summary.newsletterEmailPlaceholder')"
                    :fetch-suggestions="loadArticles"
                    :trigger-on-focus="false"
                    hide-loading
                    value-key="email"
                    @select="usePerson">
                    <template #default="{item}">
                        <div
                            class="d-flex flex-column"
                            style="line-height: 1.25rem">
                            <strong> {{ item.email }}</strong>
                            <small class="text-muted">{{ item.firstname }} {{ item.lastname }}</small>
                        </div>
                    </template>
                </el-autocomplete>
            </BaseInput>

            <template v-if="newsletterData.email">
                <BaseSelect
                    v-model="newsletterData.salutation"
                    :disabled="subscribing"
                    :name="$t('eventRegistration.summary.salutation')"
                    :placeholder="$t('eventRegistration.summary.salutation')"
                    :label="$t('eventRegistration.summary.salutation')">
                    <BaseOption
                        key="salutation_none"
                        :label="$t('eventRegistration.summary.noSalutation')"
                        :value="null">
                        <strong>{{ $t('eventRegistration.summary.noSalutation') }}</strong>
                    </BaseOption>
                    <BaseOption
                        v-for="item in salutations"
                        :key="item.title"
                        :label="item.title"
                        :value="item.title">
                        <strong>{{ item.title }}</strong>
                    </BaseOption>
                </BaseSelect>
                <BaseInput
                    v-model="newsletterData.firstname"
                    :disabled="subscribing"
                    :label="$t('eventRegistration.summary.firstname')" />
                <BaseInput
                    v-model="newsletterData.lastname"
                    :disabled="subscribing"
                    :label="$t('eventRegistration.summary.lastname')" />
                <BaseInput
                    v-if="eventData.advertisedNewsletter?.profiles?.length > 1"
                    :disabled="subscribing"
                    :label="$t('eventRegistration.summary.chosenProfiles')">
                    <div class="grid">
                        <div class="text-left d-flex gap-2">
                            <base-checkbox
                                v-for="profile in eventData.advertisedNewsletter?.profiles"
                                :key="profile.id"
                                v-model="newsletterData.profiles"
                                :value="profile.id">
                                {{ profile.description }}
                            </base-checkbox>
                        </div>
                        <quick-validator
                            :rules="{required: true}"
                            :model-value="anyProfileSelected ? newsletterData.profiles : null"
                            :name="$t('eventRegistration.summary.selectedProfiles')" />
                    </div>
                </BaseInput>
                <div
                    class="d-flex justify-content-end"
                    style="white-space: nowrap">
                    <BaseButton
                        :loading="subscribing"
                        native-type="submit"
                        :class="[{'disabled': !valid}]">
                        Jetzt abonnieren
                    </BaseButton>
                </div>
            </template>
        </VForm>
    </div>
</template>

<script>

import mainRegistrationMixin from "@/mixins/registration/mainRegistrationMixin";
import formSelectChoiceMixin from "@/mixins/registration/formSelectChoiceMixin";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

export default {
    name: "NewsletterAdvertisement",
    components: {FontAwesomeIcon},
    mixins: [mainRegistrationMixin, formSelectChoiceMixin],
    data() {
        return {
            subscribing: false,
            subscribed: false
        }
    },
    computed: {
        newsletter() {
            return this.eventData.advertisedNewsletter?.advertisable;
        },
        anyProfileSelected() {
            return this.newsletterData?.profiles?.length > 0;
        }
    },
    methods: {
        async loadArticles(searchString, cb = null) {
            if (searchString.length < 1)
                return cb ? cb([]) : []

            const personData = Object.values(this.registrationData?.persons ?? {}).filter(
                person => person.email.toLowerCase().includes(searchString.toLowerCase())
            ).map(person => ({
                id: person.id,
                salutation: person.salutation,
                email: person.email,
                firstname: person.firstname,
                lastname: person.lastname
            }));
            return cb ? cb(personData) : personData
        },
        usePerson(item) {
            if (item && item.email) {
                this.newsletterData.email = item.email;
                this.newsletterData.salutation = item.salutation;
                this.newsletterData.firstname = item.firstname;
                this.newsletterData.lastname = item.lastname;
                this.newsletterData.profiles = [];
            }
        },
        subscribeToNewsletter() {
            this.subscribing = true;
            this.$axios.post(this.$apiRoute('api.newsletters.newsletters.subscriptions.store', {newsletter: this.newsletter?.id}),
                             this.newsletterData
            ).then(() => {
                this.subscribed = true;
            }).catch(error => {
                console.log(error)
                if (error.response.status === 409) {
                    this.$notify({
                        type: 'warning',
                        message: this.$t('eventRegistration.newsletterAlreadySubscribedMessage')
                    });
                } else {
                    this.$notify({
                        type: 'warning',
                        message: this.$t('eventRegistration.newsletterSubscriptionErrorMessage')
                    });
                }
            }).finally(() => {
                this.subscribing = false;
            });
        }
    },
    watch: {
        'eventData.advertisedNewsletter': {
            handler(newValue) {
                if (newValue && !this.newsletterData) {
                    this.newsletterData = {
                        email: null,
                        salutation: null,
                        firstname: null,
                        lastname: null,
                        profiles: []
                    };
                }
            },
            immediate: true
        }
    }
}
</script>

<style lang="scss" scoped>
@use "sass:map";

.img-fit {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.grid-container {
    display: grid;
    gap: map.get($spacers, 4);
    grid-template-columns: 1fr;
    grid-template-rows: 100px auto;
    grid-template-areas:
    "image"
    "info";

    &.no-image {
        grid-template-rows: auto;
        grid-template-areas:
            "info"
    }
}

.grid-area-image {
    grid-area: image;
    margin: map.get($spacers, -3);

    @include media-breakpoint-up(md) {
        margin-right: 0;
    }
}

.grid-area-info {
    grid-area: info;
    padding: 1rem;
}

@include media-breakpoint-up(sm) {
    .grid-container {
        grid-template-columns: auto;
        grid-template-rows: 100px auto;
        grid-template-areas:
            "image"
            "info";


        &.no-image {
            grid-template-rows: auto;
            grid-template-areas:
              "info";
        }
    }
}

@include media-breakpoint-up(md) {
    .grid-container {
        grid-template-columns: 1fr 3fr;
        grid-template-rows: auto;
        grid-template-areas:
            "image info";

        &.no-image {
            grid-template-columns: 63fr 1fr;
            grid-template-areas:
                  "info not-existing-just-because-of-the-gap-column";
        }
    }

}
</style>
